import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Link,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { register } from "services/auth.service";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const theme = createTheme({
  typography: {
    fontFamily: "Inter, Arial",
  },
  palette: {
    primary: {
      main: "#4680FF",
    },
  },
});

function RegisterPage() {
  const [form, setForm] = useState({
    userName: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "error",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    if (!form.userName)
      newErrors.userName = "Tên đăng nhập không được để trống.";
    if (!form.fullName) newErrors.fullName = "Họ tên không được để trống.";
    if (!form.email) newErrors.email = "Email không được để trống.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      newErrors.email = "Email không hợp lệ.";
    if (!form.password) newErrors.password = "Mật khẩu không được để trống.";
    else if (form.password.length < 6)
      newErrors.password = "Mật khẩu tối thiểu 6 ký tự.";
    if (!form.confirmPassword)
      newErrors.confirmPassword = "Vui lòng nhập lại mật khẩu.";
    else if (form.password !== form.confirmPassword)
      newErrors.confirmPassword = "Mật khẩu nhập lại không khớp.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      await register(form);
      setAlert({
        show: true,
        message: "Đăng ký thành công! Vui lòng đăng nhập.",
        severity: "success",
      });
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          "Đăng ký thất bại. Vui lòng thử lại.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Top-left gradient */}
        <Box
          sx={{
            position: "absolute",
            top: "-20%",
            left: "-20%",
            width: "50vw",
            height: "50vw",
            background:
              "radial-gradient(circle at top left, rgba(70,128,255,0.4), transparent 70%)",
            filter: "blur(120px)",
            zIndex: 0,
          }}
        />
        {/* Middle gradient - Orange */}
        <Box
          sx={{
            position: "absolute",
            left: "30%",
            top: "20%",
            width: "40vw",
            height: "40vw",
            background:
              "radial-gradient(circle at center, rgba(255,140,0,0.35), transparent 70%)",
            filter: "blur(100px)",
            zIndex: 0,
          }}
        />
        {/* Bottom-right gradient */}
        <Box
          sx={{
            position: "absolute",
            bottom: "-20%",
            right: "-20%",
            width: "50vw",
            height: "50vw",
            background:
              "radial-gradient(circle at bottom right, rgba(70,128,255,0.4), transparent 70%)",
            filter: "blur(120px)",
            zIndex: 0,
          }}
        />
        {/* App name */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            pt: { xs: 4, sm: 8 },
            textAlign: "center",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src="/logoXNTD.png"
                alt="Logo Xí nghiệp Trắc địa"
                style={{
                  height: "80px",
                  width: "auto",
                }}
              />
            </Box>

            {/* App Name */}
            <Typography
              variant="h4"
              sx={{
                color: "#4680FF",
                fontWeight: 600,
                fontSize: { xs: "1.8rem", sm: "2rem" },
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              HỆ THỐNG
              <br />
              QUẢN LÝ XÍ NGHIỆP TRẮC ĐỊA
            </Typography>
          </Box>
        </Box>
        {/* Main content */}
        <Grid
          container
          justifyContent="center"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Grid item xs={11} sm={8} md={5} lg={4} xl={3}>
            {" "}
            <Box
              sx={{
                maxWidth: "400px",
                backgroundColor: "white",
                p: { xs: 3, sm: 4 },
                borderRadius: 2,
                boxShadow: 0,
                mx: "auto", // Căn giữa theo chiều ngang
                my: { xs: 2, sm: 3 }, // Margin theo chiều dọc
                border: "1px solid #e0e0e0", // Thêm border màu xám nhạt
              }}
            >
              <Typography
                variant="h4"
                align="center"
                gutterBottom
                sx={{
                  fontSize: { xs: "1.2rem", sm: "1.75rem" },
                  color: "#023047",
                  fontWeight: 500,
                }}
              >
                Đăng ký tài khoản
              </Typography>
              <Box component="form" noValidate onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Tên đăng nhập"
                  name="userName"
                  margin="normal"
                  value={form.userName}
                  onChange={handleChange}
                  error={!!errors.userName}
                  helperText={errors.userName}
                />
                <TextField
                  fullWidth
                  label="Họ và tên"
                  name="fullName"
                  margin="normal"
                  value={form.fullName}
                  onChange={handleChange}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  margin="normal"
                  value={form.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                />
                <TextField
                  fullWidth
                  label="Mật khẩu"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  value={form.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <Button
                        onClick={() => setShowPassword((prev) => !prev)}
                        tabIndex={-1}
                        size="small"
                        sx={{ minWidth: 0, px: 1 }}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </Button>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Nhập lại mật khẩu"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  margin="normal"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <Button
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        tabIndex={-1}
                        size="small"
                        sx={{ minWidth: 0, px: 1 }}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </Button>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ mt: 2, py: 1.2, fontSize: "1rem", boxShadow: 0 }}
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : "Đăng ký"}
                </Button>
              </Box>
              <Typography
                align="center"
                mt={2}
                sx={{ fontSize: { xs: "0.95rem", sm: "1rem" } }}
              >
                Đã có tài khoản?{" "}
                <Link href="/login" underline="hover">
                  Đăng nhập
                </Link>
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Snackbar
          open={alert.show}
          autoHideDuration={4000}
          onClose={() => setAlert({ ...alert, show: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setAlert({ ...alert, show: false })}
            severity={alert.severity}
            sx={{
              width: "100%",
              bgcolor: "background.paper",
              color:
                alert.severity === "success" ? "success.main" : "error.main",
              borderColor:
                alert.severity === "success" ? "success.light" : "error.light",
            }}
            variant="outlined"
          >
            {alert.message}
          </Alert>
        </Snackbar>
        {/* App description bottom */}
        <Box
          sx={{
            position: "fixed",
            left: 0,
            bottom: 0,
            width: "100%",
            textAlign: "center",
            pb: 2,
            zIndex: 10,
          }}
        >
          <Typography
            sx={{ color: "#515151ff", fontSize: "0.95rem", fontWeight: 600 }}
          >
            © {new Date().getFullYear()} Tổ Nghiên cứu và Phát triển - Xí nghiệp
            Trắc địa
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default RegisterPage;
